import Fuse from "fuse.js";
import difference from "lodash/difference";
import get from "lodash/get";
import merge from "deepmerge";
import StorageClient from "../../clients/storageClient";
import { Workspace, ActiveWorkspace, WorkspaceImport, GroupData } from "./types";
import { identity, isEqualIgnoringArrayOrder, camelCase } from "../../common/util";
import { WORKSPACE } from "../../common/constants";
import { WindowStorageManager, CompleteWindowState, StateType } from "./windowStorageManager";
import { FinsembleWindowData } from "../window/types";
import ConfigClient from "../../clients/configClient";
import { LoggerClient as Logger } from "../../clients/logger";
import { clone } from "lodash";
import uuidv4 from "uuid-random";

const {
	STORAGE_TOPIC,
	CACHE_STORAGE_TOPIC,
	CLEAN_SHUTDOWN,
	ACTIVE_WORKSPACE,
	ALL_WORKSPACES,
	LAST_USED_WORKSPACE_NAME,
} = WORKSPACE;

/**
 * A trace object passed through the call chain
 * to better debug events as they proceed through
 * the layers of the service.
 *
 * See notes in WorkspaceService as to why this is necessary.
 *
 * NOTE: DO NOT JUST MAKE UP A TRACE. The WorkspaceService
 *
 * This should probably be moved to ./src/common at some point.
 */
export type Trace = {
	/** A unique counter incremented on each API request. */
	counter?: number;
	/** The reason for the API (typically the name API channel). */
	reason: string;
};

/**
 * Additional options that can be added to an add request.
 * If the addition of the workspace is because of a rename operation,
 * the order of the workspace list should be maintained. The adding of the workspace
 * needs to know when an addition is made to maintain that order
 * (splicing the 'new' workspace for the old)
 *
 */
type addWorkspaceOpts = {
	force?: boolean;

	isRename?: boolean;

	oldName?: string;
};

/**
 * Composes an array of functions together, producing
 * a new function that is the result of applying each
 * function from right to left on its arguments.
 *
 * @example
 * const add1 = x => x + 1;
 * const multiply3 = x => x * 3
 * const mulityply3Add1 = composeRL(add1, multiply3);
 * mulityply3Add1(4); // => 13
 */
export const composeRL = (...fns) =>
	fns.reduce(
		(f, g) =>
			(...args) =>
				f(g(...args))
	);

/**
 * Convenience function, given a trace and other arguments,
 * auto-formats a log message appropriately.
 */
function log({ counter, reason }: Trace, ...args) {
	Logger.system.debug(`TRACE-${counter}-${reason}`, ...args);
}

/**
 * Given either a workspace name or a workspace object,
 * returns the storage key for that workspace.
 * @param workspace A workspace object or the name of a workspace.
 */
export const getWorkspaceStorageKey = (workspace: Workspace | string): string =>
	typeof workspace === "string" ? camelCase(workspace) : camelCase(workspace.name);

/**
 * Given a requested workspace name and the list of existing workspace
 * names, returns the name with "(n)" appended to it, where n is the
 * number of prior names with the same pattern.
 *
 * NOTE - this function assumes the requested name already exists in the
 * list of workspace names.
 *
 * See tests for more examples.
 *
 * @example
 * getNextWorkspaceName("foo", ["foo"]) // => "foo (1)"
 * getNextWorkspaceName("bar", ["bar", "bar (1)"]) // => "bar (2)"
 */
export function getNextWorkspaceName(name: string, workspaceNames: string[]): string {
	const endsInDigit = /\(\d+\)$/;
	const nameMinusDigit = name.replace(endsInDigit, "").trim();
	const digits = [...workspaceNames, name]
		.filter((x) => camelCase(x).includes(camelCase(nameMinusDigit)))
		.map((n) => {
			const digit = n.match(endsInDigit);
			return digit === null
				? 0
				: // Slice off the parens
				  Number(digit[0].slice(1, -1));
		});
	const nextDigitInSeries = Math.max(...digits) + 1;
	return `${nameMinusDigit} (${nextDigitInSeries})`;
}

export const NOT_INITIALIZED = "You must wait for the Workspace Manager to initialize before taking this action.";
export const REMOVE_ACTIVE_WORKSPACE = "You cannot remove the active workspace";
export const setOrderError = (oldOrder) =>
	`A new workspace order must include an entry for every workspace. Old order: ${oldOrder}`;
export const workspaceNotFoundError = (ws) => `Workspace "${ws}" not found in storage.`;
export const notValidWindowNameError = (win) => `"${win}" is not a valid window name`;

export const NO_ACTIVE_WORKSPACE = "Attempt to interact with the active workspace before an active workspace was set.";
/**
 * Static class for interacting with workspace storage.
 *
 * WorkspaceManager is an ORM-like interface into the storage
 * service as it pertains to workspaces. It enforces consistency
 * and our business logic around workspaces; as such, all
 * interactions with storage pertaining to workspace should
 * be run through this class.
 *
 * WorkspaceManager provides three kinds of functions:
 * a) functions for dealing with individual workspaces (such as adding windows,
 * setting the group data, etc.)
 * b) functions for working on the set of workspaces (adding workspaces,
 * removing, renaming, etc.)
 * c) functions for dealing with a special workspace called the active workspace,
 * which gets its own location and storage.
 *
 * All three of these are similar and coupled enough to warrant handling in
 * the same class; but the WorkspaceManager should never grow beyond this scope.
 *
 * DH 3/11/2019
 * Because the class is static, it could technically be safely
 * imported and used from anywhere; however, at present, only
 * the workspace service uses the manager, and it will likely
 * stay this way until Workspaces are stored in a reactive store
 * like the upcoming Persistent Store.
 *
 * A Note on Performance:
 * WorkspaceManager doesn't cache anything, which necessitates many
 * round trips to the StorageService despite no changes in data. In
 * my own testing, I haven't seen this to be an issue; however, if
 * client's storage implementation is very slow, it could feasibly
 * create some drag (though I doubt it would  be the bottleneck). At
 * the cost of added complexity, a caching mechanism could be added
 * overtop the functions without changing their API. However, I think
 * a reactive store like the Persistent store is a better answer to
 * this problem - it's simpler (no extra caching logic specific to
 * workspaces), more flexible (provides new ways for clients to
 * interact with the data), and more performant (entirely push based,
 * instead of pull).
 *
 * KW 25/11/21 Caching mechansim described above has been added
 * to eliminate excessive queries to storage that are generating an awesome
 * amount of logging and generally acting as a bottleneck.
 */
export class WorkspaceManager {
	/**
	 * If true, the active workspace is saved to
	 * the store on every update.
	 */
	static autosave = false;

	static promptUserOnDirtyWorkspace = true;

	static discardUnsavedChanges = false;

	/**
	 * Daniel H. - 2/26/2019
	 * There is some bizarre effect whereby the fake store
	 * doesn't get reset correctly unless you manually set the
	 * reference before each test. For this reason alone, I had
	 * to make StorageClient and WindowStorageManager public
	 * members; I would much rather just reference them directly
	 * since they're singletons.
	 *
	 * Note, the WindowStorageManger suffers this same problem
	 * under test.
	 */
	static _SC = StorageClient;

	static _WS = WindowStorageManager;

	private static fsblUuid: string;

	/** Cached active workspace,
	 *  maintained by updateActiveWorkspace, getActiveWorkspace, setActiveWorkspace, saveActiveWorkspace, updateActiveWorkspace
	 */
	static cachedActiveWorkspace: any;

	/** Cached active workspace name,
	 *  maintained by setActiveWorkspace
	 */
	static cachedActiveWorkspaceName: string;

	/** Cached workspaces,
	 *  maintained by getWorkspace, addWorkspace, removeWorkspace, updateWorkspace.
	 */
	static cachedWorkspaces: {} = {};

	/** Cached Workspace names,
	 *  maintained by getWorkspaceNames, addWorkspace, removeWorkspace, setWorkspaceOrder. */
	static cachedWorkspaceNames: string[] = [];

	static ignoreProps: Array<string>;

	/**
	 * A copy of the workspace config converted to an object lookup. This is used to annotate workspaces with their latest config information. This
	 * is set once, so it does not update with dynamic config but will update with each reboot of Finsemble.
	 */
	static workspaceConfigs = {};

	/**
	 * Given window data, gets a new name for a window.
	 *
	 * Windows coming from workspace configuration may not have a name; we
	 * must therefore add one with the same naming scheme used by Launcher.
	 */
	private static getNewWindowName(win: FinsembleWindowData): string {
		return `${win.componentType}-${uuidv4()}`;
	}

	/**
	 * Asynchronously updates provided key in storage by first retrieving the key
	 * then running a provided function on the result and re-saving its value.
	 * There’s no guarantees of consistency or atomicity
	 *
	 * Migrated from StorageClient (as originally planned) to facilitate access to
	 * mutated values for caching purposes.
	 *
	 * This function should be eliminated in future as cached values can  be used instead
	 * of retrieving and mutating values from storage.
	 *
	 * @param params {any} Update storage params
	 * @param params.topic {string} The storage topic
	 * @param params.key {string} The storage key
	 * @param params.updateFn {Function} Function to run to determine the value to store
	 * @private
	 */
	private static async updateStorage(params: { topic: string; key: string; updateFn: (x: any) => any }) {
		const { topic, key, updateFn } = params;
		const result = await WorkspaceManager._SC.get({ topic, key });
		const updatedValue = updateFn(result);
		const { err, data } = await WorkspaceManager._SC.save({ topic, key, value: updatedValue });

		return { err, data: updatedValue };
	}

	/**
	 * Adds a workspace to the system, persisting it to storage. If the requested
	 * name is already in use and "force" is set to be false, a workspace with a different
	 * name will be generated.
	 *
	 * Returns the final name of the workspace in storage as a string.
	 */
	static async addWorkspace(trace: Trace, ws: Workspace, opts: addWorkspaceOpts = {}): Promise<string> {
		const { force = false, isRename = false, oldName = null } = opts;
		log(trace, "Adding workspace", ws, "force:", force);
		// Temporary fix for illegal workspace names
		if (ws.name === "") {
			ws.name = "Untitled";
		}
		const workspaceNames = await WorkspaceManager.getWorkspaceNames(trace);
		const nameAlreadyExists = workspaceNames.some((workspace) => camelCase(workspace) === camelCase(ws.name));

		if (nameAlreadyExists && force) {
			await WorkspaceManager.removeWorkspace(trace, ws.name);
		}

		const name = nameAlreadyExists && !force ? getNextWorkspaceName(ws.name, workspaceNames) : ws.name;

		const { err, data: allWorkspaces } = await this.updateStorage({
			topic: STORAGE_TOPIC,
			key: ALL_WORKSPACES,
			updateFn: (workspaces) => {
				if (workspaces) {
					if (isRename) {
						// If this is a rename we need to find the index of the old workspace and do a replacement
						const oldWorkspaceIndex = workspaces.findIndex((workspace) => workspace === oldName);
						if (oldWorkspaceIndex >= 0) workspaces.splice(oldWorkspaceIndex, 1, name);
						else workspaces.push(name); // If the index is not found push onto the end of the set

						return workspaces;
					} else {
						// If not a rename, add to the end of the set
						return [...workspaces, name];
					}
				} else {
					// Return new array if no existing workspaces
					return [name];
				}
			},
		});
		if (err) {
			Logger.system.error("Failed to update storage when adding a new workspace!", err);
		}

		const { err: err2, data: data2 } = await this.updateStorage({
			topic: CACHE_STORAGE_TOPIC,
			key: ALL_WORKSPACES,
			updateFn: (workspaces) => allWorkspaces, //use value computed in previous update
		});
		if (err2) {
			Logger.system.error("Failed to update (cache) storage when adding a new workspace!", err2);
		}

		//cache workspace names locally
		WorkspaceManager.cachedWorkspaceNames = allWorkspaces;

		const finalWs = {
			...ws,
			// Use the new name
			name,
		};

		//cache the workspace locally
		WorkspaceManager.cachedWorkspaces[name] = finalWs;

		//and save it to storage
		await WorkspaceManager._SC.save({
			topic: STORAGE_TOPIC,
			key: getWorkspaceStorageKey(finalWs),
			value: finalWs,
		});

		log(trace, "Workspace added");
		return name;
	}

	/**
	 * NOTE! This bypasses the set-dirty logic. For most operations, just use
	 * the public updateWorkspace method (you can even omit the workspace name
	 * to automatically target the active workspace).
	 */
	static async updateActiveWorkspace(trace: Trace, updateFn: (ws: ActiveWorkspace) => ActiveWorkspace): Promise<void> {
		log(trace, "Updating active workspace.");
		Logger.system.debug("update-trace updateActiveWorkspace", Logger.callStack());
		const { err, data } = await this.updateStorage({
			topic: CACHE_STORAGE_TOPIC,
			key: ACTIVE_WORKSPACE,
			updateFn,
		});
		//update local cache of active workspace
		WorkspaceManager.cachedActiveWorkspace = data;

		log(trace, "Active workspace updated");
	}

	/**
	 * Updates windowData for windows in the activeWorkspace.
	 */
	static async updateWindowDataForActiveWorkspace(windows: FinsembleWindowData[], aws: Workspace) {
		for (const winData of windows) {
			await WorkspaceManager._WS.setState("windowData", winData.name, winData, aws.name);
		}
	}

	/**
	 * Converts window data from the previous versions of Finsemble
	 * to the current version. Should only be used by importWorkspace.
	 *
	 * @param windowData An object that will be formatted to a `FinsembleWindowData` object
	 * NOTE! This this object should be compatible!
	 */
	private static formatWindow(windowData: any): FinsembleWindowData {
		const correctedData = {
			...windowData,
		} as FinsembleWindowData;
		const formatOptions = function (options: any): any {
			const propertyConversionMap = new Map([
				["defaultTop", "top"],
				["defaultBottom", "bottom"],
				["defaultLeft", "left"],
				["defaultRight", "right"],
				["defaultWidth", "width"],
				["defaultHeight", "height"],
			]);
			const updatePropertyOnNewOptions = function (oldKey, currentKey) {
				correctedData[currentKey] = options[currentKey] || options[oldKey];

				if (correctedData[currentKey] === undefined) {
					delete correctedData[currentKey];
				}

				delete correctedData[oldKey];
			};

			propertyConversionMap.forEach(updatePropertyOnNewOptions);
			delete correctedData.options;
		};

		correctedData.componentType =
			windowData.componentType || get(windowData, "customData.component.type", undefined) || windowData.type;

		delete correctedData.type;
		correctedData.name = correctedData.name || WorkspaceManager.getNewWindowName(correctedData);

		if (windowData.options !== undefined) {
			formatOptions(windowData.options);
		}

		return correctedData;
	}

	/**
	 * This takes the output of getManyCompleteWindowStates and then splits them into windowData and componentState arrays.
	 * @param completeStates
	 */
	private static splitCompleteStates(completeStates) {
		const windowData: any[] = [];
		const componentState: any[] = [];
		for (const state of completeStates) {
			windowData.push(state.windowData);
			componentState.push(state.componentState);
		}
		return {
			windowData,
			componentState,
		};
	}

	/**
	 * Imports workspace data into storage.
	 *
	 * This data can be in any of the supported data format versions, and the necessary
	 * transformations will be applied automatically (see the `formatWindow`).
	 *
	 * @param workspace A workspace import object, a string representing a workspace import
	 * object as JSON.
	 */
	static async importWorkspace(trace: Trace, workspace: string | WorkspaceImport, force = false): Promise<string> {
		const { components, windowData, componentStates, ...ws } =
			typeof workspace === "string" ? JSON.parse(workspace) : workspace;
		let wsWindows;

		if (windowData !== undefined) {
			wsWindows = windowData;
		} else {
			wsWindows = [...(ws.windows || []), ...(components || [])];
		}

		const windows: FinsembleWindowData[] = wsWindows.map(WorkspaceManager.formatWindow);

		const workspaceNames = await WorkspaceManager.getWorkspaceNames(trace);
		const nameAlreadyExists = workspaceNames.some((workspaceName) => camelCase(workspaceName) === camelCase(ws.name));
		if (nameAlreadyExists) {
			if (force) {
				await WorkspaceManager.removeWorkspace(trace, ws.name);
			} else {
				ws.name = getNextWorkspaceName(ws.name, workspaceNames);
			}
		}

		for (const winData of windows) {
			await WorkspaceManager._WS.setCompleteState(
				{
					windowData: winData as FinsembleWindowData,
					componentState: get(componentStates, [winData.name], {}),
				},
				ws.name
			);
		}

		const createdWorkspace = {
			...ws,
			windows: windows.map((x: FinsembleWindowData) => x.name),
		};
		return WorkspaceManager.addWorkspace(trace, createdWorkspace);
	}

	/**
	 * Retrieves the given workspace from the local cache.
	 * @param workspaceName The name of the workspace to retrieve.
	 * @param storageLocation optional storage location (STORAGE_TOPIC or CACHE_STORAGE_TOPIC)
	 */
	static async getWorkspace(
		trace: Trace,
		workspaceName: string,
		storageLocation: string = STORAGE_TOPIC
	): Promise<Workspace> {
		log(trace, "Getting workspace", workspaceName, storageLocation);

		//KW: 25/11/21 previously was retrieving workspace from storage on every request, which was a bottlneck
		let data = WorkspaceManager.cachedWorkspaces[workspaceName];

		if (!data) {
			//try retrieving from storage instead
			const key = getWorkspaceStorageKey(workspaceName);
			data = await WorkspaceManager._SC.get({
				key,
				topic: storageLocation,
			});
			if (data) {
				WorkspaceManager.cachedWorkspaces[workspaceName] = data;
			}
		}

		if (!data) {
			throw new Error(workspaceNotFoundError(workspaceName));
		}

		//disabled log message due to memory use concerns (every workspace is logged multiple times on each workspaceService API call)
		//log(trace, "Workspace retrieved", data);
		const workspaceWithConfig = {
			...WorkspaceManager?.workspaceConfigs?.[workspaceName],
			...data,
		};
		return workspaceWithConfig as Workspace;
	}

	/**
	 * Stores the config for workspaces. Config members are added to workspaces in getWorkspace().
	 *
	 * @param workspaceConfig Array<Workspace> The config for workspaces (the `finsemble.workspaces` entry in config)
	 */
	static setWorkspaceConfig(workspaceConfig: Array<any>) {
		workspaceConfig.forEach((config) => {
			WorkspaceManager.workspaceConfigs[config.name] = config;
		});
	}

	/**
	 * Exports a workspace from storage in `WorkspaceImport` format.
	 * @param workspaceName The name of the workspace to retrieve from storage.
	 */
	static async exportWorkspace(trace: Trace, workspaceName: string, toast: Function): Promise<WorkspaceImport> {
		const ws = await WorkspaceManager.getWorkspace(trace, workspaceName);
		const [windows, windowData, componentStates] = (
			await WorkspaceManager._WS.getManyCompleteStates(ws.windows, ws.name)
		)
			// TS 2.9.2 can't correctly infer the types from .reduce
			.reduce(
				(prev: [string[], FinsembleWindowData[], Record<string, any>], { componentState, windowData: data }) => {
					const [names, wStates, cStates] = prev;
					try {
						return [[...names, data.name], [...wStates, data], { ...cStates, [data.name]: componentState }];
					} catch (error) {
						toast(
							"user",
							"ALWAYS",
							"FSBL-Workspace-ExportError",
							"The workspace data has been corrupted. Check the Central Logger for any errors"
						);
						Logger.system.error(error);
						return [[], [], {}];
					}
				},
				[[], [], {}]
			);
		// Ditto above comment about TS 2.9.2
		return {
			...ws,
			windows,
			windowData,
			componentStates,
		} as WorkspaceImport;
	}

	/**
	 * Retrieves all workspaces from storage.
	 */
	static async getWorkspaces(trace: Trace): Promise<(Workspace | null)[]> {
		const workspaceNames = (await WorkspaceManager.getWorkspaceNames(trace)) || [];
		return (
			await Promise.all(workspaceNames.map((x) => WorkspaceManager.getWorkspace(trace, x).catch(() => null)))
		).filter(identity);
	}

	/**
	 * Returns true if last shutdown was dirty. False is returned if the field is undefined in storage the first time Finsemble runs.
	 */
	static async wasLastShutdownDirty(trace: Trace): Promise<boolean> {
		log(trace, "wasLastShutdownDirty");
		const params = { topic: CACHE_STORAGE_TOPIC, key: CLEAN_SHUTDOWN };
		let cleanShutdownValue: boolean = await WorkspaceManager._SC.get(params);
		log(trace, "wasLastShutdownDirty value", cleanShutdownValue === false ? "false" : "true");
		return cleanShutdownValue === false;
	}

	/**
	 * Sets state to track if last shutdown was dirty
	 */
	static async setCleanShutdownFlag(trace: Trace, flag: boolean): Promise<void> {
		log(trace, "setCleanShutdownFlag", flag ? "true" : "false");
		const params = { topic: CACHE_STORAGE_TOPIC, key: CLEAN_SHUTDOWN, value: flag };
		await WorkspaceManager._SC.save(params);
	}

	/**
	 * saves the name of the last used workspace into master storage.
	 */
	static async saveLastUsedWorkspaceName(trace: Trace, workspaceName: string): Promise<void> {
		log(trace, "Saving last used workspace name");
		const params = {
			topic: STORAGE_TOPIC,
			key: LAST_USED_WORKSPACE_NAME,
			value: workspaceName,
		};
		await WorkspaceManager._SC.save(params);
	}

	/**
	 * Retrieves the name of the last used workspace from master storage.
	 */
	static async getLastUsedWorkspaceName(trace: Trace): Promise<string> {
		log(trace, "Getting last used workspace name");
		const params = { topic: STORAGE_TOPIC, key: LAST_USED_WORKSPACE_NAME };
		let name: string = await WorkspaceManager._SC.get(params);
		return name;
	}

	/**
	 * Retrieves the names of all workspaces in storage.
	 */
	static async getWorkspaceNames(trace: Trace, fromCache: boolean = true): Promise<string[]> {
		//KW 25/11/21: theres seems little point in maintaining double storage of this list
		//switching cached retrieval to local cache
		log(trace, `Getting ${fromCache ? "cached" : ""} workspace names`);

		if (!fromCache || !WorkspaceManager.cachedWorkspaceNames || WorkspaceManager.cachedWorkspaceNames.length == 0) {
			const params = { topic: STORAGE_TOPIC, key: ALL_WORKSPACES };
			let names = await WorkspaceManager._SC.get(params);
			WorkspaceManager.cachedWorkspaceNames = names ? names : [];
		}

		return WorkspaceManager.cachedWorkspaceNames;
	}

	/**
	 * Retrieves the state of active workspace, which may be retrieved from storage or the local cache.
	 */
	static async getActiveWorkspace(trace: Trace, fromCache: boolean = true): Promise<ActiveWorkspace> {
		log(trace, `Getting active workspace${fromCache ? " from cache" : ""}`);

		if (!fromCache || !WorkspaceManager.cachedActiveWorkspace) {
			let aws = (await WorkspaceManager._SC.get({
				topic: CACHE_STORAGE_TOPIC,
				key: ACTIVE_WORKSPACE,
			})) as ActiveWorkspace;
			WorkspaceManager.cachedActiveWorkspace = aws;
		}

		if (!WorkspaceManager.cachedActiveWorkspace) {
			Logger.system.debug("Active workspace retrieved is null", WorkspaceManager.cachedActiveWorkspace);
			throw new Error(NO_ACTIVE_WORKSPACE);
		}
		log(trace, "Active workspace retrieved", WorkspaceManager.cachedActiveWorkspace);
		return WorkspaceManager.cachedActiveWorkspace;
	}

	/**
	 * Returns a boolean indicating whether an active workspace has been set.
	 */
	static async doesActiveWorkspaceExist(trace: Trace): Promise<boolean> {
		/**
		 * Garbee 3/11/2019
		 * It may seem odd that we are returning the result of a promise chain into a new promise.
		 * The reason for this is to avoid the calling coder from being confused.
		 * If we don't await this and return the chain, then this is all inserted in their chain.
		 * Since we catch, this could lead to confusing results for them if their code errors as well.
		 */
		return await WorkspaceManager.getActiveWorkspace(trace)
			.then(() => true)
			.catch(() => false);
	}

	/**
	 * Returns a boolean indicating whether the active workspace is
	 * currently dirty.
	 */
	static async isActiveWorkspaceDirty(trace: Trace): Promise<boolean> {
		if (!WorkspaceManager.ignoreProps) {
			try {
				const ignoreProps = await ConfigClient.getValue(
					"finsemble.servicesConfig.window.ignoreFieldsForDirtyingWorkspace"
				);
				WorkspaceManager.ignoreProps = ignoreProps.data || [];
			} catch {
				WorkspaceManager.ignoreProps = [];
			}
		}
		const { ...aws } = await WorkspaceManager.getActiveWorkspace(trace);
		const states = await WorkspaceManager._WS.getManyCompleteStates(aws.windows);
		const { normalizedWorkspace, normalizedWindowStates } = await this.normalizeWorkspace(trace, aws, states);
		const { windowData, componentState } = WorkspaceManager.splitCompleteStates(normalizedWindowStates);
		const fullActiveWorkspace: any = normalizedWorkspace;
		fullActiveWorkspace.windowData = windowData;
		fullActiveWorkspace.componentState = componentState;
		//we used to add a guid property to the active workspace - remove it if it exists
		delete fullActiveWorkspace.guid;
		for (const windowDatum of fullActiveWorkspace.windowData) {
			for (const prop of WorkspaceManager.ignoreProps) {
				delete windowDatum[prop];
			}
		}

		//compare to the stored workspace (which should already be normalised)
		const storedWorkspace = await WorkspaceManager.getWorkspace(trace, aws.name);
		const storedWorkspaceClone: any = clone(storedWorkspace);
		const storedStates = await WorkspaceManager._WS.getManyCompleteStates(storedWorkspaceClone.windows, aws.name);
		const { normalizedWorkspace: normalizedStoredWorkspace, normalizedWindowStates: normalizedStoredWindowStates } =
			await this.normalizeWorkspace(trace, storedWorkspaceClone, storedStates);
		const { windowData: storedWindowData, componentState: storedComponentState } =
			WorkspaceManager.splitCompleteStates(normalizedStoredWindowStates);
		const fullNormalizedStoredWorkspace: any = normalizedStoredWorkspace;
		fullNormalizedStoredWorkspace.windowData = storedWindowData;
		fullNormalizedStoredWorkspace.componentState = storedComponentState;
		delete fullNormalizedStoredWorkspace.guid;
		for (const windowDatum of fullNormalizedStoredWorkspace.windowData) {
			for (const prop of WorkspaceManager.ignoreProps) {
				delete windowDatum[prop];
			}
		}

		Logger.system.debug(
			"isActiveWorkspaceDirty",
			"stored workspace",
			fullNormalizedStoredWorkspace,
			"activeWorkspace",
			fullActiveWorkspace
		);
		return !isEqualIgnoringArrayOrder(fullNormalizedStoredWorkspace, fullActiveWorkspace);
	}

	/**
	 * Sets the active workspace to the workspace with the given name.
	 * Returns the value of the workspace in a promise.
	 *
	 * @param workspaceName The name of the workspace in storage to
	 * make the active workspace.
	 */
	static async setActiveWorkspace(
		trace: Trace,
		workspaceName: string,
		cacheOnly: boolean = false
	): Promise<ActiveWorkspace> {
		log(trace, "Setting active workspace", workspaceName, cacheOnly ? "cacheOnly=true" : "cacheOnly=false");
		let activeWorkspace;
		let newName;
		let workspace;

		// set this value immediately in case error below -- it will be updated again below
		WorkspaceManager.cachedActiveWorkspaceName = workspaceName;

		if (cacheOnly) {
			activeWorkspace = await WorkspaceManager.getWorkspace(trace, workspaceName, CACHE_STORAGE_TOPIC);
			newName = activeWorkspace.name;
		} else {
			if (await WorkspaceManager.doesActiveWorkspaceExist(trace)) {
				// So as not to leave behind orphans, we remove all the states in
				// the active workspace before saving.
				activeWorkspace = await WorkspaceManager.getActiveWorkspace(trace);
				await WorkspaceManager._WS.removeManyCompleteStates(activeWorkspace.windows);
			}

			try {
				Logger.system.debug("setActiveWorkspace reading workspace", workspaceName);
				const newActive = await WorkspaceManager.getWorkspace(trace, workspaceName);
				const states = await WorkspaceManager._WS.getManyCompleteStates(newActive.windows, workspaceName);
				const { windowData, componentState } = WorkspaceManager.splitCompleteStates(states);
				Logger.system.debug("setActiveWorkspace reading workspace states", states, windowData, componentState);

				for (const s of states) {
					await WorkspaceManager._WS.setCompleteState(s);
				}

				WorkspaceManager.cachedActiveWorkspace = newActive;
				//KW 2021 12 10: Stopped adding states to the workspace object as they aren't really supposed to be there and are not kept up to date
				// WorkspaceManager.cachedActiveWorkspace.windowData = clone(windowData);
				// WorkspaceManager.cachedActiveWorkspace.componentState = clone(componentState);

				workspace = WorkspaceManager.cachedActiveWorkspace;
				newName = workspace.name;
			} catch (error) {
				Logger.system.error("setActiveWorkspace error", error);
				throw new Error("setting active workspace failed");
			}

			Logger.system.debug("update-trace setActiveWorkspace", Logger.callStack());
			await WorkspaceManager._SC.save({
				//KW 21/11/21
				//Removed the guid property throughout as we're not using it
				value: { ...workspace },
				topic: CACHE_STORAGE_TOPIC,
				key: ACTIVE_WORKSPACE,
			});
			log(trace, "Active workspace set.");
		}

		// this cached workspace name is the first step in creating a memory cache for the active workspace, which will
		// resolve some async problems and performance problems
		WorkspaceManager.cachedActiveWorkspaceName = newName;

		return workspace;
	}

	/**
	 * Saves the state of active workspace into storage, overwriting
	 * what state was there under the workspace with the same as the
	 * active workspace.
	 *
	 * No-ops if the active workspace isn't dirty.
	 */
	static async saveActiveWorkspace(trace: Trace): Promise<void> {
		log(trace, "Saving active workspace");

		const { ...aws } = await WorkspaceManager.getActiveWorkspace(trace);

		try {
			// Clear out all the window states to prevent orphans
			const oldWindows = (await WorkspaceManager.getWorkspace(trace, aws.name)).windows;
			await WorkspaceManager._WS.removeManyCompleteStates(difference(oldWindows, aws.windows), aws.name);
		} catch (calculateDifferenceErr) {
			Logger.system.error("Encountered an error while comparing workspaces for differences.", calculateDifferenceErr);
		}

		const states = await WorkspaceManager._WS.getManyCompleteStates(aws.windows);
		const { normalizedWorkspace, normalizedWindowStates } = await this.normalizeWorkspace(trace, aws, states);
		// Replaces all the state of the workspace in storage
		// with all the state of the active workspace
		try {
			await WorkspaceManager.updateWorkspace(trace, () => normalizedWorkspace, normalizedWorkspace.name);
		} catch (updateWSErr) {
			Logger.system.error("Error updating workspace.", updateWSErr);
		}

		try {
			await WorkspaceManager._WS.setManyCompleteStates(normalizedWindowStates, aws.name);
			WorkspaceManager.cachedActiveWorkspace = aws;

			//KW 2021 12 10: Stopped adding states to the workspace object as they aren't really supposed to be there and are not kept up to date
			//const { windowData, componentState } = WorkspaceManager.splitCompleteStates(normalizedWindowStates);
			// WorkspaceManager.cachedActiveWorkspace.windowData = clone(windowData);
			// WorkspaceManager.cachedActiveWorkspace.componentState = clone(componentState);
		} catch (updateWindowStatesErr) {
			Logger.system.error("Error updating window states when switching workspaces.", updateWindowStatesErr);
		}

		log(trace, "Active workspace saved");
	}

	/**
	 * Saves the current state of the active workspace as a new workspace
	 * with the given name.
	 *
	 * @param name The new name of the workspace. If the same as the
	 * active workspace, the active workspace will merely be saved
	 * If force is false, a new name will be generated from the given
	 * name and used instead.
	 *
	 * @param force Forces an overwrite of a workspace with the same name.
	 */
	static async saveActiveWorkspaceAs(trace: Trace, name: string, force: boolean): Promise<string | undefined> {
		let aws;
		try {
			log(trace, `Saving active workspace workspace as "${name}". Force param is ${force}.`);

			// Destructuring pulls out the unnecessary "guid" prop.
			({ ...aws } = await WorkspaceManager.getActiveWorkspace(trace));
		} catch (getActiveWorkspaceErr) {
			Logger.system.error("Error getting active workspace for saveAs.", getActiveWorkspaceErr);
		}

		if (aws.name === name) {
			log(trace, "New name is same as the current name. Saving the active workspace.");
			try {
				await WorkspaceManager.saveActiveWorkspace(trace);
			} catch (saveActiveWSErr) {
				Logger.system.error("Error saving active workspace.", saveActiveWSErr);
			} finally {
				return;
			}
		}

		let finalName;
		try {
			const winStates = await WorkspaceManager._WS.getManyCompleteStates(aws.windows);
			const { normalizedWorkspace, normalizedWindowStates } = await this.normalizeWorkspace(trace, aws, winStates);
			finalName = await WorkspaceManager.addWorkspace(trace, { ...normalizedWorkspace, name }, { force });
			await WorkspaceManager._WS.setManyCompleteStates(normalizedWindowStates, finalName);
		} catch (updateWindowStatesErr) {
			Logger.system.error("Error setting window states when saving workspace.", updateWindowStatesErr);
		}

		try {
			await WorkspaceManager.setActiveWorkspace(trace, finalName);
		} catch (setActiveWSErr) {
			Logger.system.error("Error setting active workspace.", setActiveWSErr);
		}

		return finalName;
	}

	/**
	 * Ensures that evergreen components have sequential names without gaps to prevent bogus dirty detection
	 * @param workspace The workspace to normalize
	 */
	private static async normalizeWorkspace(
		trace: Trace,
		workspace: ActiveWorkspace,
		winStates: CompleteWindowState[]
	): Promise<{ normalizedWorkspace: ActiveWorkspace; normalizedWindowStates: CompleteWindowState[] }> {
		log(trace, `Normalizing workspace`, workspace);

		// iterate over windows to find evergreen components
		const evergreenComponents = {};
		if (winStates) {
			winStates.forEach((windowState) => {
				if (windowState.windowData && windowState.windowData.isEvergreen) {
					if (!evergreenComponents[windowState.windowData.componentType]) {
						evergreenComponents[windowState.windowData.componentType] = [];
					}

					evergreenComponents[windowState.windowData.componentType].push(windowState.windowData.name);
				}
			});
		}

		// Map names from existing name to new name
		const nameMap: any = {};
		Object.keys(evergreenComponents).forEach((componentType) => {
			evergreenComponents[componentType].sort();
			let count = 1;
			evergreenComponents[componentType].forEach((origName) => {
				const newName = `${componentType}-${count}`;
				nameMap[origName] = newName;
				count = count + 1;
			});
		});

		// Rename existing names to new name
		let workspaceStr = JSON.stringify(workspace);
		let winStatesStr = JSON.stringify(winStates);

		// First pass rename
		const renameMap = {};
		Object.keys(nameMap).forEach((origName) => {
			const newName = nameMap[origName];
			const uuid = uuidv4();
			renameMap[uuid] = newName;
			workspaceStr = workspaceStr.replace(origName, uuid);
			winStatesStr = winStatesStr.replace(origName, uuid);
		});

		// second pass rename
		Object.keys(renameMap).forEach((origName) => {
			const newName = renameMap[origName];
			workspaceStr = workspaceStr.replace(origName, newName);
			winStatesStr = winStatesStr.replace(origName, newName);
		});

		const normalizedWorkspace: ActiveWorkspace = JSON.parse(workspaceStr);
		const normalizedWindowStates: CompleteWindowState[] = JSON.parse(winStatesStr);

		return { normalizedWorkspace, normalizedWindowStates };
	}

	/**
	 * Adds a new, empty workspace to storage with the given name
	 * and returns the final name used (if the name already exists,
	 * a new name will generated using the standard naming scheme
	 * (see `getNextWorkspaceName`).
	 */
	static async newWorkspace(trace: Trace, workspaceName: string): Promise<string> {
		log(trace, "Creating new workspace", workspaceName);
		const name = await WorkspaceManager.addWorkspace(trace, {
			version: "1.0.0",
			name: workspaceName,
			windows: [],
			type: "workspace",
			groups: {},
		});
		log(trace, "New workspace created");
		return name;
	}

	/**
	 * Changes the name of the workspace with `oldName` to `newName`.
	 * If `newName` already exists, a new name will be generated using
	 * the standard naming scheme (see `getNextWorkspaceName`).
	 *
	 * If the names are the same, the method is a no-op.
	 */
	static async renameWorkspace(trace: Trace, newName: string, oldName: string): Promise<string> {
		log(trace, `Renaming workspace "${oldName}" to "${newName}"`);
		// Renaming to the same name should be a noop.
		if (newName === oldName) return Promise.resolve(newName);

		const aws = await WorkspaceManager.getActiveWorkspace(trace);
		const targetingActiveWorkspace = oldName === aws.name;

		const ws = await WorkspaceManager.getWorkspace(trace, oldName);

		const finalName = await WorkspaceManager.addWorkspace(trace, { ...ws, name: newName }, { isRename: true, oldName });
		log(trace, `Final name of rename: ${finalName}`);
		const states = await WorkspaceManager._WS.getManyCompleteStates(ws.windows, oldName);
		log(trace, "Window states", states);
		await WorkspaceManager._WS.setManyCompleteStates(states, finalName);

		if (targetingActiveWorkspace) {
			const aStates = await WorkspaceManager._WS.getManyCompleteStates(aws.windows);
			await WorkspaceManager.setActiveWorkspace(trace, finalName);
			await WorkspaceManager._WS.setManyCompleteStates(aStates);

			await WorkspaceManager.updateActiveWorkspace(trace, (activeWorkspace) => {
				return { ...activeWorkspace, name: finalName };
			});
		}

		await WorkspaceManager.removeWorkspace(trace, oldName);

		return finalName;
	}

	/**
	 * Removes the workspace from storage.
	 *
	 * NOTE: you cannot remove the active workspace.
	 * @param workspaceName The name of the workspace to remove.
	 */
	static async removeWorkspace(trace: Trace, workspaceName: string): Promise<void> {
		log(trace, "Removing workspace", workspaceName);
		const aws = await WorkspaceManager.getActiveWorkspace(trace);
		const targetOnlySelectedWorkspace = (workspaces: string[]): string[] =>
			workspaces.filter((workspace) => getWorkspaceStorageKey(workspace) !== getWorkspaceStorageKey(workspaceName));

		if (aws && workspaceName === aws.name) {
			throw new Error("You cannot remove the active workspace");
		}

		const ws = await WorkspaceManager.getWorkspace(trace, workspaceName);

		// clear out local caches
		WorkspaceManager.cachedWorkspaceNames = WorkspaceManager.cachedWorkspaceNames.filter((value, index, arr) => {
			return value != workspaceName;
		});
		delete WorkspaceManager.cachedWorkspaces[workspaceName];

		// Clear out the window states.
		await WorkspaceManager._WS.removeManyCompleteStates(ws.windows, ws.name);

		await WorkspaceManager._SC.remove({
			topic: STORAGE_TOPIC,
			key: getWorkspaceStorageKey(workspaceName),
		});

		await this.updateStorage({
			topic: STORAGE_TOPIC,
			key: ALL_WORKSPACES,
			updateFn: targetOnlySelectedWorkspace,
		});
		log(trace, "Workspace removed");

		await this.updateStorage({
			topic: CACHE_STORAGE_TOPIC,
			key: ALL_WORKSPACES,
			updateFn: targetOnlySelectedWorkspace,
		});
	}

	/**
	 * Searches for a workspace that fuzzy-matches the given string.
	 * Returns results according to
	 * https://documentation.finsemble.com/tutorial-Search.html
	 *
	 * @param s The string to fuzzy match against the workspace names.
	 */
	static async searchWorkspaces(trace: Trace, s: string) {
		/**
		 * Fuse's type definitions aren't up-to-date,
		 * so we have to do some casting here.
		 */
		type FuseResults = {
			item: string;
			matches: { indices: any; value: string }[];
			score: number;
		};

		const options = await WorkspaceManager.getWorkspaceSearchConfig(trace);
		if (!options.enabled) {
			return [] as FuseResults[];
		}

		const workspaces = await WorkspaceManager.getWorkspaceNames(trace);
		return (<FuseResults[]>(<any>new Fuse(workspaces, options).search(s))).map(({ matches, score, item }) => {
			return {
				score,
				matches,
				name: item,
				type: "Workspace",
				description: "",
				actions: [{ name: "Switch" }],
				tags: [],
			};
		});
	}

	/**
	 * Gets the "finsemble.workspaceSearch" configuration, merges it with the default and returns the combined object.
	 *
	 * @param {Trace} trace Unused for now - Debug trace
	 * @returns {Promise<any>} Returns a workspace config object
	 */
	static async getWorkspaceSearchConfig(trace: Trace): Promise<any> {
		log(trace, "Getting 'finsemble.workspaceSearch' config");
		let options = {
			enabled: true,
			shouldSort: true,
			includeScore: true,
			includeMatches: true,
			threshold: 0.4,
			location: 0,
			distance: 100,
			maxPatternLength: 32,
			minMatchCharLength: 1,
		};
		const { data: configWorkspaceSearch } = await ConfigClient.getValue({
			field: "finsemble.servicesConfig.workspace.search",
		});
		if (configWorkspaceSearch != null) {
			if (configWorkspaceSearch.hasOwnProperty("enabled")) {
				options.enabled = configWorkspaceSearch.enabled;
			}
			if (configWorkspaceSearch.hasOwnProperty("options")) {
				options = merge(options, configWorkspaceSearch.options);
			}
		}
		return options;
	}

	/**
	 * Updates the state of a workspace in storage by retrieving the current state,
	 * applying the given transformation function, and setting the state to the result.
	 * Omit `workspaceName` to target the active workspace.
	 *
	 * If autosave is on and you are targeting the active workspace, the new state of the
	 * active workspace will automatically be persisted to permanent storage.
	 *
	 * @param updateFn A function that accepts a workspace and returns a transformed workspace.
	 * @param workspaceName The name of the workspace to update.
	 *
	 * DH 3/11/2019
	 * It would be nice if this function no-oped if there were no difference between the
	 * current state and the resulting state. This would eliminate some of the code in methods
	 * that use this function.
	 *
	 * KW 25/11/21
	 * Added update of local cache of workspaces to reduce reliance on storage
	 */
	private static async updateWorkspace(
		trace: Trace,
		updateFn: (ws: Workspace) => Workspace,
		workspaceName: string = ACTIVE_WORKSPACE
	) {
		log(trace, "Updating workspace", workspaceName);
		const targetingActiveWorkspace = workspaceName === ACTIVE_WORKSPACE;
		const awsExists = await WorkspaceManager.doesActiveWorkspaceExist(trace);

		if (targetingActiveWorkspace && !awsExists) {
			throw new Error(NO_ACTIVE_WORKSPACE);
		}

		//clean out any unwanted data (e.g. windowData, componentState, guid) here so that they don't leak into storage (in an out of date form)
		const removeExtraneousData = (ws: Workspace) => {
			let cleanWs: any = { ...ws };
			delete cleanWs.windowData;
			delete cleanWs.componentState;
			delete cleanWs.guid;
			return cleanWs;
		};
		const cleanAndUpdate = composeRL(removeExtraneousData, updateFn);

		const aws = awsExists ? await WorkspaceManager.getActiveWorkspace(trace) : null;
		if (targetingActiveWorkspace && aws) {
			const update = cleanAndUpdate(aws);
			log(trace, "Updated workspace", update);
			// Exit early if the update wouldn't produce any changes.
			if (isEqualIgnoringArrayOrder(update, aws)) {
				log(trace, "Update and current state are equal. Dropping request");
				return;
			}
			log(trace, "Update workspace is targeting active workspace");
			Logger.system.debug("update-trace updateWorkspace", Logger.callStack());

			//update local cache
			WorkspaceManager.cachedActiveWorkspace = update;

			//update storage
			await WorkspaceManager._SC.save({
				topic: CACHE_STORAGE_TOPIC,
				key: ACTIVE_WORKSPACE,
				value: { ...update },
			});
			log(trace, "Active workspace updated");
		}

		if (!targetingActiveWorkspace || WorkspaceManager.autosave) {
			log(trace, "Persisting workspace data to storage", aws);

			//update storage
			//if update was produced above (most common case) we could use that here (apply turnOffDirtyFlagIfActive and clone it) which would avoid an extra retrieval updateStorage
			const name = targetingActiveWorkspace && aws ? aws.name : workspaceName;
			const { err, data: updatedWorkspace } = await this.updateStorage({
				topic: STORAGE_TOPIC,
				key: getWorkspaceStorageKey(name),
				updateFn: cleanAndUpdate,
			});
			log(trace, "Workspace data persisted to storage", aws);

			//update local cache
			WorkspaceManager.cachedWorkspaces[name] = updatedWorkspace;
		}
		log(trace, "Workspace updated");
	}

	/**
	 * Updates the state of a window in storage by retrieving the current state,
	 * applying the given transformation function, and setting the state to the result.
	 *
	 * If this results in a change, the active workspace will be dirtied (or the change
	 * will be persisted to permanent storage if autosave is on).
	 *
	 * DH 5/21/2019 This is a complex function, with an exception type
	 * (if called before an active workspace is set), and 3 possible
	 * exits: if the states are totally equal, if the states are equal after
	 * removing cruft keys, and if the states are genuinely different. Before
	 * we can safely remove these checks, a larger refactoring in stacks/assimilated windows
	 * (or some other architectural change) will be necessary.
	 */
	static async updateWindowState(trace: Trace, stateVar: StateType, updateFn, windowName: string): Promise<void> {
		log(trace, "updating window state");

		if (!(await WorkspaceManager.doesActiveWorkspaceExist(trace))) {
			throw new Error(NO_ACTIVE_WORKSPACE);
		}

		const oldState = await WorkspaceManager._WS.getState(stateVar, windowName);
		const update = updateFn(oldState);
		if (isEqualIgnoringArrayOrder(oldState, update)) {
			log(trace, "updating window state -- deep equal", oldState, update);
			// If there is no difference at all, we don't need to do anything.
			return;
		} else {
			log(trace, "updating window state -- NOT deep equal", oldState, update);
		}

		await WorkspaceManager._WS.setState(stateVar, windowName, update);

		const aws = await WorkspaceManager.getActiveWorkspace(trace);

		if (
			// We don't want windows not in the workspace to dirty it.
			!aws.windows.includes(windowName) ||
			// We also need to strip off random keys that are constantly changing without reason.
			// By better defining what should and shouldn't be saved, this can be eliminated.

			//KW 25/11/21: its not clear to me what this check is protecting us from, only affects the autosave below
			// would make sense to
			// Further, if the below is true I think we will have already returned above...
			isEqualIgnoringArrayOrder(update, oldState)
		) {
			log(trace, "updating window state -- ignoring");
			return;
		}

		// If Autosave is on, we need to save the update to the permanent storage.
		if (WorkspaceManager.autosave) {
			log(trace, "updating window state -- autosave");
			await WorkspaceManager._WS.setState(stateVar, windowName, update, aws.name);
		}
	}

	/**
	 * Given a window state, persists that window state to the active workspace storage.
	 * NOTE: This completely clobbers any existing state for that window.
	 *
	 * If this results in a change, the active workspace will be dirtied (or the change
	 * will be persisted to permanent storage if autosave is on).
	 */
	static async setWindowState(trace, windowName, state: Partial<CompleteWindowState>) {
		log(trace, "setWindowState", windowName, state);

		for (const key of Object.keys(state)) {
			await WorkspaceManager.updateWindowState(trace, key as StateType, () => state[key], windowName);
		}
	}

	/**
	 * Retrieves a window state from the active workspace storage.
	 */
	static async getWindowState(trace, windowName: string, stateVars: StateType[]) {
		log(trace, "getWindowState", windowName, stateVars);
		let out = {};
		for (const key of stateVars) {
			out[key] = await WorkspaceManager._WS.getState(key as StateType, windowName);
		}
		return out;
	}

	/**
	 * Passthrough to WindowStorageManager.
	 */
	static async getManyStates(
		type: StateType,
		windowNames: string[],
		workspaceName?: string
	): Promise<FinsembleWindowData[]> {
		return WorkspaceManager._WS.getManyStates(type, windowNames, workspaceName);
	}

	/**
	 * Adds a window to a given workspace.
	 * Omit `workspaceName` to target the active workspace.
	 */
	static async addWindowToWorkspace(
		trace: Trace,
		win: FinsembleWindowData | string,
		workspaceName?: string
	): Promise<void> {
		if (!win) throw new Error(notValidWindowNameError(win));
		if (typeof win === "object" && !win.name) throw new Error(notValidWindowNameError(win.name));

		log(trace, "Adding window", win, "to workspace", workspaceName || ACTIVE_WORKSPACE);
		const isString = typeof win === "string";
		const name = isString ? (win as string) : (win as FinsembleWindowData).name;

		const state: CompleteWindowState = {
			windowData: win as FinsembleWindowData,
			componentState: {},
		};

		if (!isString) await WorkspaceManager._WS.setCompleteState(state, workspaceName);

		Logger.system.debug("update-trace addWindowToWorkspace", Logger.callStack());

		await WorkspaceManager.updateWorkspace(
			trace,
			(ws) => {
				// DH 3/18/2019 - May not be necessary to filter since `updateWorkspace` compares the results...
				const windowsMinusTarget = ws.windows.filter((window) => window !== name);
				return {
					...ws,
					windows: [...windowsMinusTarget, name],
				};
			},
			workspaceName
		);
		log(trace, `Window ${name} added to ${workspaceName || ACTIVE_WORKSPACE}`);
	}

	/**
	 * Removes a window from a given workspace.
	 * Omit `workspaceName` to target the active workspace.
	 */
	static async removeWindowFromWorkspace(trace: Trace, windowName: string, workspaceName?: string): Promise<void> {
		if (!windowName) throw new Error(notValidWindowNameError(windowName));
		log(trace, `Removing window ${windowName} from workspace ${workspaceName || ACTIVE_WORKSPACE}`);
		try {
			await WorkspaceManager._WS.removeCompleteState(windowName, workspaceName);
		} catch (error) {
			Logger.system.warn(`Failed to remove window state ${windowName} from workspace ${workspaceName}.`);
		}
		Logger.system.debug("update-trace removeWindowFromWorkspace", Logger.callStack());

		await WorkspaceManager.updateWorkspace(
			trace,
			(ws) => {
				const filtered = ws.windows.filter((x) => x !== windowName);
				log(trace, "Filtered windows,", filtered);
				return { ...ws, windows: filtered };
			},
			workspaceName
		);
		log(trace, `Window ${windowName} removed from workspace ${workspaceName || ACTIVE_WORKSPACE}`);
	}

	/**
	 * Sets the group data of a given workspace.
	 * Omit the `workspaceName` to target the active workspace.
	 *
	 * DH 3/11/2019
	 * Note the lack of fine-grained control here compared to
	 * the other workspace functions (i.e Windows, which you can
	 * add and remove individually). I think this is an indication
	 * this method doesn't belong here.
	 *
	 * @param groups An object where the keys are the group guids and the
	 * values are the group data objects.
	 */
	static async setGroupData(trace: Trace, groups: Record<string, GroupData>, workspaceName?) {
		try {
			const ws = workspaceName
				? await WorkspaceManager.getWorkspace(trace, workspaceName)
				: await WorkspaceManager.getActiveWorkspace(trace);
			/** DH 7/8/2019
			 * For some reason, when switching workspaces, docking publishes group updates before
			 * the switch, as well as after (neither should be necessary, since the workspace
			 * is the source of group data on load). There is a race condition in workspaceService.ts
			 * such that certain `setGroupData` commands are processed at the wrong time in relation
			 * to the `setActiveWorkspace` command during `handleSwitchTo`, causing the group update
			 * from the previous active workspace to wrongly be applied to the current active workspace
			 * (and then immediately undone by the the next message, which contains the correct groups)
			 * Correcting the race condition is as simple as using:
			 * `await limit(() => WSM.setActiveWorkspace(trace, name);`
			 * However, there is a circular dependency between the window service and workspace
			 * service (workspace can't switch till docking finishes, docking can't finish to new
			 * active workspace is set), resulting in deadlock.
			 * Therefore, for the time being, we'll for now resort to optimistic concurrency: to
			 * update group data, you must provide the guid of the workspace you wish to update. If the
			 * guid doesn't match the guid of the current active workspace, the update is discarded.
			 */

			// MM 2/21/2020
			// discard late income setGroup requests that applied to the previous workspace;
			// using the cachedWorkspace name (instead of active workspace.guid) because there's sync issues with retrieving active workspace from storage

			//KW 21/11/21
			//removed the guid property throughout as we're not using it
			if (
				WorkspaceManager.cachedActiveWorkspaceName &&
				(ws as ActiveWorkspace).name !== WorkspaceManager.cachedActiveWorkspaceName
			) {
				Logger.system.debug(
					"update-trace setGroupData workspace name mismatch",
					ws.name,
					WorkspaceManager.cachedActiveWorkspaceName,
					groups,
					Logger.callStack()
				);
			} else {
				Logger.system.debug("update-trace setGroupData", ws.name, groups, Logger.callStack());
				await WorkspaceManager.updateWorkspace(
					trace,
					(workspace) => {
						return { ...workspace, groups };
					},
					workspaceName
				);
			}
		} catch (err) {
			Logger.system.debug("update-trace setGroupData err", err);
		}
	}

	/**
	 * Sets the order of workspaces in storage to the given order.
	 * Used to control the order of the names as they appear in Finsemble's UI.
	 */
	static async setWorkspaceOrder(trace: Trace, newOrder: string[]): Promise<void> {
		// Users should include an entry for every workspace in the new order.
		const oldEntries = await WorkspaceManager.getWorkspaceNames(trace);

		// Make a copy here, otherwise you'll mutate the array.
		if (!isEqualIgnoringArrayOrder([...newOrder], [...oldEntries])) {
			throw new Error(setOrderError(oldEntries));
		}

		// To reduce read/write to remote storage, we save the workspace name list
		// in to a local cache, and the cache topic as well as remotely. This way the cache topic will receive updates
		// immediately allowing the UI to move on/make updates while the remote storage is updated

		WorkspaceManager.cachedWorkspaceNames = newOrder;

		/**
		 * 7/27/20 JC: The reason we need to reduce the read/write to remote is certain operations
		 * cause these read/writes to happen too frequently and any kind of slowdown will mess with
		 * UI operations. This can really be seen with stacked windows when switching tabs with a slow
		 * remote storage/adapter
		 */
		await WorkspaceManager._SC.save({
			topic: CACHE_STORAGE_TOPIC,
			key: ALL_WORKSPACES,
			value: newOrder,
		});

		await WorkspaceManager._SC.save({
			topic: STORAGE_TOPIC,
			key: ALL_WORKSPACES,
			value: newOrder,
		});
	}

	/**
	 * Transitions pre-3.9 workspace storage schemes to the new scheme,
	 * by pulling in workspaces from the old storage key, "fsblWorkspaces",
	 * and also adding a guid to the active workspace.
	 *
	 * //KW 21/11/21
	 * Removed the guid property throughout as we're not using it
	 */
	static async importLegacyWorkspaces(trace: Trace) {
		const location = { topic: WORKSPACE.STORAGE_TOPIC, key: "fsblWorkspaces" };
		const workspaces = (await WorkspaceManager._SC.get(location)) || [];
		for (const { name, groups, windows } of workspaces) {
			const ws: Workspace = {
				name,
				groups,
				windows,
				type: "workspace",
				version: "1.0.0",
			};
			await WorkspaceManager.addWorkspace(trace, ws);
		}
		await WorkspaceManager._SC.remove(location);

		if (await WorkspaceManager.doesActiveWorkspaceExist(trace)) {
			// updateActiveWorkspace is used here as opposed to updateWorkspace
			// so as not to inadvertently dirty it.
			await WorkspaceManager.updateActiveWorkspace(trace, (ws) => {
				return { ...ws };
			});
		}
	}
}
